import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import { formatTemplateDetailDate } from './dailyMenuApi';
import CateringTemplateItemsEditor from './CateringTemplateItemsEditor';
import { fetchCateringTempById } from './cateringTempApi';
import './css/Home.css';
import './css/Menu.css';
import './css/DailyMenu.css';
import './css/Inventory.css';
import './css/Restaurants.css';
import './css/OrderDetails.css';

function formatStatusDisplay(active) {
    if (active === true || active === 1 || active === '1') return 'Active';
    if (active === false || active === 0 || active === '0') return 'Inactive';
    const s = String(active ?? '').trim().toLowerCase();
    if (!s) return '—';
    return s.charAt(0).toUpperCase() + s.slice(1);
}

function isActiveStatus(status) {
    return (
        status === true ||
        status === 1 ||
        status === '1' ||
        String(status ?? '').trim().toLowerCase() === 'active' ||
        String(status ?? '').trim().toLowerCase() === 'true'
    );
}

function formatPricePerPerson(value) {
    if (value == null || value === '') return '—';
    const n = Number.parseFloat(value);
    if (!Number.isFinite(n)) return String(value);
    return `₹${n.toFixed(2)}`;
}

const displayValue = (value) => {
    const text = value != null ? String(value).trim() : '';
    return text || '—';
};

const DetailField = ({ label, value, wide = false, children }) => (
    <div className={`restaurant-detail-field${wide ? ' restaurant-detail-field-wide' : ''}`}>
        <span className="restaurant-detail-label">{label}</span>
        {children ?? <span className="restaurant-detail-value">{displayValue(value)}</span>}
    </div>
);

function CateringTemplateDetailRouteRedirect({ cateringTempId }) {
    const [searchParams] = useSearchParams();
    const restaurantId = searchParams.get('restaurantId');
    let to = `/home?tab=restaurants/mytemplates/${cateringTempId}`;
    if (restaurantId) {
        to += `&restaurantId=${encodeURIComponent(restaurantId)}`;
    }
    return <Navigate to={to} replace />;
}

const CateringTemplateDetailView = ({ cateringTempId, embedded = false }) => {
    const [searchParams] = useSearchParams();
    const restaurantId = searchParams.get('restaurantId') || localStorage.getItem('restaurantId');

    const [pkg, setPkg] = useState(null);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const user = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('user') || 'null');
        } catch (e) {
            return null;
        }
    }, []);

    const loadPackage = useCallback(async () => {
        const id = Number(cateringTempId);
        if (!cateringTempId || Number.isNaN(id)) {
            setError('Invalid package id.');
            setLoading(false);
            return;
        }
        setLoading(true);
        setError('');
        try {
            const data = await fetchCateringTempById(id);
            setPkg(data);
            setItems(Array.isArray(data?.items) ? data.items : []);
        } catch (err) {
            console.error('Package detail fetch failed:', err?.response?.data || err?.message);
            setError(
                err?.response?.data?.message ||
                    err?.response?.data?.error ||
                    'Failed to load package.'
            );
            setPkg(null);
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, [cateringTempId]);

    useEffect(() => {
        loadPackage();
    }, [loadPackage]);

    const detailBody = (
        <div className="menu menu-template menu-template-detail-page">
            <div className="restaurant-detail-panel">
                <div className="restaurant-detail-card">
                    <div className="restaurant-detail-card-header menu-template-detail-card-header">
                        <h3 className="restaurant-detail-section-title">Package information</h3>
                    </div>

                    {loading ? <p className="order-details-loading">Loading package…</p> : null}
                    {!loading && error ? (
                        <p className="restaurant-detail-message error">{error}</p>
                    ) : null}
                    {!loading && !error && pkg ? (
                        <div className="restaurant-detail-grid">
                            <DetailField label="Package name" value={pkg.packageName} />
                            <DetailField
                                label="Price per person"
                                value={formatPricePerPerson(pkg.pricePerPerson)}
                            />
                            <DetailField label="Status">
                                <span
                                    className={`menu-template-status-badge menu-template-status-badge--${
                                        isActiveStatus(pkg.status) ? 'active' : 'inactive'
                                    }`}
                                >
                                    {formatStatusDisplay(pkg.status)}
                                </span>
                            </DetailField>
                            <DetailField label="Package ID" value={pkg.cateringTempId} />
                            <DetailField
                                label="Description"
                                value={pkg.description}
                                wide
                            />
                            <DetailField
                                label="Created"
                                value={formatTemplateDetailDate(pkg.createdDate)}
                            />
                            <DetailField
                                label="Last updated"
                                value={formatTemplateDetailDate(pkg.updatedDate)}
                            />
                        </div>
                    ) : null}
                    {!loading && !pkg && !error ? (
                        <p className="order-details-empty">No detail data for this package.</p>
                    ) : null}
                </div>

                {!loading && !error && pkg ? (
                    <div className="restaurant-detail-card">
                        <CateringTemplateItemsEditor
                            restaurantId={restaurantId}
                            items={items}
                            onItemsChange={setItems}
                            packageId={cateringTempId}
                            user={user}
                            disabled={loading}
                            headerClassName="restaurant-detail-card-header menu-template-items-editor-header"
                        />
                    </div>
                ) : null}
            </div>
        </div>
    );

    if (embedded) {
        return detailBody;
    }

    return (
        <div className="home-container menu-template-detail-page">
            <div className="tab-content">{detailBody}</div>
        </div>
    );
};

function CateringTemplateDetail(props) {
    const { cateringTempId: cateringTempIdProp, embedded = false } = props;
    const { cateringTempId: routeCateringTempId, templateId: routeTemplateId } = useParams();
    const resolvedId = cateringTempIdProp ?? routeCateringTempId ?? routeTemplateId;

    if (!embedded && resolvedId && cateringTempIdProp == null) {
        return <CateringTemplateDetailRouteRedirect cateringTempId={resolvedId} />;
    }

    return (
        <CateringTemplateDetailView
            {...props}
            cateringTempId={resolvedId}
        />
    );
}

export default CateringTemplateDetail;
